import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Navigation, AlertCircle } from 'lucide-react';
import ContactForm from '@/components/ContactForm';

const useInView = (threshold = 0.1) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const ContactPage: React.FC = () => {
  const { ref: formRef, inView: formInView } = useInView(0.05);

  const contactCards = [
    {
      icon: <MapPin className="w-5 h-5 text-[color:var(--svh-crest)]" />,
      title: 'Adresse',
      lines: ['Bayreuther Str. 47', '95500 Heinersreuth'],
    },
    {
      icon: <Phone className="w-5 h-5 text-[color:var(--svh-crest)]" />,
      title: 'Telefon',
      lines: [
        <a href="tel:+4992148388" className="hover:text-white transition-colors" key="tel">
          +49 921 48388
        </a>
      ]
    },
    {
      icon: <Mail className="w-5 h-5 text-[color:var(--svh-crest)]" />,
      title: 'E-Mail',
      lines: ['info@svheinersreuth-padel.de'],
    },
    {
      icon: <Clock className="w-5 h-5 text-[color:var(--svh-crest)]" />,
      title: 'Öffnungszeiten',
      lines: ['Mo – So: 8:00 – 23:00 Uhr', 'Flutlicht bis 23:00 Uhr'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden bg-black">
        <img
          src="/photos/tennis-ball.jpg"
          alt="Contact"
          className="absolute inset-0 w-full h-full object-cover scale-[1.06] opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-16 sm:pb-20 px-6 sm:px-10 max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-7 h-[1.5px] bg-[color:var(--svh-crest)]" />
              <span className="svh-label" style={{ color: 'rgba(255,255,255,0.62)' }}>SV Heinersreuth</span>
            </div>
            <h1 className="svh-display-xl mb-5" style={{ color: '#FFFFFF' }}>
              Kontakt
            </h1>
            <p className="svh-lead max-w-lg" style={{ color: 'rgba(255,255,255,0.78)' }}>
              Wir helfen Ihnen bei allen Fragen rund um Padel, Tennis & Buchungen.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="svh-band svh-band--ink py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 -mt-1">
            {contactCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="py-7 px-6 border-r border-b lg:border-b-0 border-white/10 last:border-r-0"
              >
                <div className="w-10 h-10 rounded-[10px] flex items-center justify-center mb-5" style={{ background: 'rgba(232,40,40,0.14)' }}>
                  {card.icon}
                </div>
                <h3 className="svh-label mb-2.5">{card.title}</h3>
                <div className="space-y-0.5">
                  {card.lines.map((line, j) => (
                    <p key={j} className="text-[15px] text-white/85 leading-relaxed">{line}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section ref={formRef} className="svh-band svh-band--canvas svh-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mb-10">
                <div className="svh-opener__tag mb-5">
                  <span className="svh-label">Kontaktformular</span>
                </div>
                <h2 className="svh-display-lg mb-4">
                  Nachricht senden
                </h2>
                <p className="svh-body max-w-sm">
                  Nutzen Sie das Formular für Anfragen, Feedback oder einen Buchungswunsch.
                </p>
              </div>
              <div className="svh-card svh-card-lg svh-card-raised p-6 sm:p-8">
                <ContactForm />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-5"
            >
              <div className="rounded-[16px] overflow-hidden border border-[color:var(--svh-border)] shadow-[var(--shadow-sm)]">
                <div className="h-56 sm:h-64">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2577.8!2d11.5449!3d49.9539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDnCsDU3JzE0LjAiTiAxMcKwMzInNDEuNiJF!5e0!3m2!1sde!2sde!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="SV Heinersreuth Standort"
                  />
                </div>
                <div className="p-5 sm:p-6 bg-white">
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="w-4 h-4 text-[color:var(--svh-crest)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[13px] mb-0.5">SV Heinersreuth Tennis & Padel</p>
                      <p className="text-[13px] text-[color:var(--svh-ink-3)]">Bayreuther Str. 47, 95500 Heinersreuth</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href="https://www.google.com/maps/search/Bayreuther+Str.+47,+95500+Heinersreuth"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-[12px] font-semibold rounded-xl transition-colors"
                    >
                      <MapPin className="w-3 h-3" />
                      Google Maps
                    </a>
                    <button
                      onClick={() => {
                        const dest = "Bayreuther Str. 47, 95500 Heinersreuth";
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition((pos) => {
                            const url = `https://www.google.com/maps/dir/${pos.coords.latitude},${pos.coords.longitude}/${encodeURIComponent(dest)}`;
                            window.open(url, '_blank');
                          });
                        } else {
                          window.open(`https://www.google.com/maps/dir//${encodeURIComponent(dest)}`, '_blank');
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-[12px] font-semibold rounded-xl hover:border-red-500 hover:text-[color:var(--svh-crest)] transition-all"
                    >
                      <Navigation className="w-3 h-3" />
                      Route planen
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[16px] border border-[color:var(--svh-border)] shadow-[var(--shadow-sm)] p-5 sm:p-6">
                <h3 className="text-[13px] font-bold text-gray-900 mb-4 tracking-tight">Anfahrt</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Auto', text: 'Direkt an der B85, kostenlose Parkplätze vorhanden' },
                    { label: 'ÖPNV', text: 'Buslinie 302, Haltestelle "Heinersreuth Ortsmitte"' },
                    { label: 'Fahrrad', text: 'Gut erreichbar über Radwege entlang der Bayreuther Straße' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <span className="text-[10px] font-bold text-red-500/70 uppercase tracking-[0.14em] w-14 flex-shrink-0 pt-0.5">{item.label}</span>
                      <p className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-[16px] p-5 sm:p-6 border border-[color:var(--svh-border)] shadow-[var(--shadow-sm)]">
                <div className="flex items-start gap-3 mb-3">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <h3 className="text-[13px] font-bold text-gray-900">Notfallkontakt</h3>
                </div>
                <p className="text-[13px] text-[color:var(--svh-ink-3)] mb-4 leading-relaxed pl-7">
                  Für dringende Angelegenheiten außerhalb der Sprechzeiten:
                </p>
                <div className="space-y-2 pl-7">
                  <a href="tel:+491608831195" className="block text-[13px] font-semibold text-gray-600 hover:text-[color:var(--svh-crest)] transition-colors">
                    +49 160 8831195
                  </a>
                  <a href="tel:+491633140762" className="block text-[13px] font-semibold text-gray-600 hover:text-[color:var(--svh-crest)] transition-colors">
                    +49 163 3140762
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
